import React from "react";
import { Link } from "gatsby";
import { twMerge } from "tailwind-merge";

export type ButtonVariant =
  | "primary"
  | "primary_green"
  | "primary_white"
  | "primary_filled"
  | "secondary";

const Button = ({
  children,
  className,
  variant = "primary",
  onClick,
  internalPath,
  disabled,
  elements,
}: ButtonProps) => {
  const internalPathValue = internalPath ?? elements?.internal_path?.value;
  const externalUrl = elements?.external_url?.value;
  const target = elements?.open_in_new_window?.value[0]?.codename;
  const buttonVariant = elements?.variant.value[0].codename ?? variant;

  const getButtonClass = (variant: ButtonVariant) => {
    // Styles located in global.css
    switch (variant) {
      case "primary_green":
        return "btn-primary-green";
      case "primary_filled":
        return "btn-primary-filled";
      case "primary_white":
        return "btn-primary-white";
      case "secondary":
        return "btn-secondary";
      default:
        return "btn-primary";
    }
  };

  const btnClass = getButtonClass(buttonVariant);

  const renderLink = () => {
    if (internalPathValue) {
      return (
        <Link
          to={internalPathValue}
          target={target}
          onClick={onClick}
          className={twMerge(btnClass, className)}
        >
          {children}
        </Link>
      );
    } else if (externalUrl) {
      const href = externalUrl[0] === "+" ? `tel:${externalUrl}` : externalUrl;
      return (
        <a
          href={href}
          target={target}
          onClick={onClick}
          className={twMerge(btnClass, className)}
        >
          {children}
        </a>
      );
    } else {
      return (
        <button
          onClick={onClick}
          className={twMerge(btnClass, className)}
          disabled={disabled}
        >
          {children}
        </button>
      );
    }
  };

  return renderLink();
};

export interface ButtonProps extends Partial<KontentButton> {
  children: React.ReactNode;
  variant?: ButtonVariant;
  className?: string;
  onClick?: () => void;
  internalPath?: string;
  disabled?: boolean;
}

export default Button;

// Kontent Schema
export interface KontentButton {
  elements: {
    text: { value: string };
    variant: {
      value: [{ codename: ButtonVariant }];
    };
    open_in_new_window: { value: [{ codename: string }] };
    internal_path: { value: string };
    external_url: { value: string };
  };
  system: { type: "button" };
}
